import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { fetchMegaphoneCampaignOrders } from '@services/index';
import { PaginatedResponse } from '@app/types/common';
import { MegaphoneCampaignOrder } from '@insights_module/types/Megaphone';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';

export const useInfiniteMegaphoneCampaignOrders = (
  campaignId: string | null,
  searchTerm?: string
) => {
  const { isEnabled: isMegaphoneTrackingPixelEnabled } = useFeatureFlag(
    FEATURE_FLAG.MEGAPHONE_TRACKING_PIXEL
  );
  const [totalMegaphoneCampaignOrders, setTotalMegaphoneCampaignOrders] = useState<number>(0);
  const [campaignOrders, setCampaignOrders] = useState<MegaphoneCampaignOrder[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

  const { data, isLoading } = useInfiniteQuery({
    queryKey: ['megaphone-campaign-orders', { id: campaignId, keyword: searchTerm }],
    queryFn: ({ pageParam }) =>
      fetchMegaphoneCampaignOrders({
        page: pageParam,
        pageSize: 500,
        id: campaignId as string,
        keyword: searchTerm,
      }),
    getNextPageParam: ({ pagination, pageParam }) => {
      const { total, page_size } = pagination;
      const hasMore = total / page_size > pageParam;
      return hasMore ? pageParam + 1 : undefined;
    },
    getPreviousPageParam: ({ pageParam }) => {
      return pageParam > 1;
    },
    enabled: !!campaignId && isMegaphoneTrackingPixelEnabled,
    staleTime: 60 * 60 * 1000, // 1h
  });

  useEffect(() => {
    if (!campaignId) {
      setIsInitialLoading(true);
      setCampaignOrders([]);
    }
  }, [campaignId]);

  useEffect(() => {
    setIsInitialLoading(true);
    setCampaignOrders([]);
  }, [searchTerm]);

  useEffect(() => {
    if (data?.pages?.length) {
      setTotalMegaphoneCampaignOrders(data.pages[0].pagination.total);
      const arr: MegaphoneCampaignOrder[] = [];
      data.pages?.forEach((element: PaginatedResponse<MegaphoneCampaignOrder>) => {
        element.data.map((item: MegaphoneCampaignOrder) => arr.push(item));
      });

      setCampaignOrders(arr);
      setIsInitialLoading(false);
    } else {
      setCampaignOrders([]);
    }
  }, [data?.pages]);

  const { availableOrdersCount, trackedOrdersCount, limitedOrdersCount } = useMemo(() => {
    return campaignOrders.reduce(
      (acc, order) => {
        if (order.sounderTrackingUrlMax && !order.sounderTrackingUrlSet) {
          acc.limitedOrdersCount += 1;
          return acc;
        }

        if (order.sounderTrackingUrlSet) {
          acc.trackedOrdersCount += 1;
          return acc;
        }

        acc.availableOrdersCount += 1;
        return acc;
      },
      {
        availableOrdersCount: 0,
        trackedOrdersCount: 0,
        limitedOrdersCount: 0,
      }
    );
  }, [campaignOrders]);

  return {
    campaignOrders,
    totalMegaphoneCampaignOrders,
    isLoading,
    isInitialLoading,
    availableOrdersCount,
    trackedOrdersCount,
    limitedOrdersCount,
  };
};
